import { IData } from '../../../../Assembler/dist/registry/iface/iData.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { TimeFilter } from '../../registryCommon/dynamicData/timeFilter.js';

/**
 * Dynamic data provider that returns current time
 *
 * The param reads, the instant resolution and the formatting all live in TimeFilter — the shared pure
 * producer — so this server provider, TimeClientData and the client processor cannot drift from one
 * another.
 */
export class TimeData extends IData {
    /**
     * Gets the full type name for DynamicData operation tracking
     */
    get typeName(): string {
        return 'TimeData';
    }

    /**
     * Produces the dynamic data in a single server-side pass.
     * TimeData generates a value directly and does not participate in a raw-data template flow.
     */
    getData(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject {
        let retDataJson = new JsonObject();

        try {
            // These are per-call LOCALS, not fields. Handlers are registered once and invoked as singletons,
            // so a field would carry one AppSite's DateFormat/IsLocalTime into the next component whose param
            // file omits it — a cross-AppSite leak that makes the render depend on merge order. FixedTime
            // would inherit exactly that bug, and a determinism param that is itself order-dependent is worse
            // than none, so all three are read fresh per call.
            const timeKey = TimeFilter.readTimeKey(paramJson);
            const dateFormat = TimeFilter.readDateFormat(paramJson);
            const isLocalTime = TimeFilter.readIsLocalTime(paramJson);
            const fixedTime = TimeFilter.readFixedTime(paramJson);

            if (componentJson !== null && componentJson !== undefined) {
                // Support Map, wrapper objects with has/containsKey, and plain objects
                const hasTimeKey = TimeFilter.hasKey(componentJson, timeKey);
                if (hasTimeKey) {
                    const instant = TimeFilter.resolveCurrentTime(fixedTime, isLocalTime);
                    retDataJson = TimeFilter.buildTimeResult(timeKey,
                        TimeFilter.formatCurrentTime(instant, dateFormat));
                }
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'TimeData');
        }

        return retDataJson;
    }
}
